import { i18n } from '@fs/zion-locale'
import { languageConfig } from '@fs/zion-rootstech'

// IETF language tags combine subtags from other standards such as ISO 639, ISO 15924, ISO 3166-1 and UN M.49.
// We are currently only concerned with the country code ISO 639-1 and 639-2

// Web-only SEO alias data for hreflang normalization
const hreflangLanguageAliases = { jp: 'ja' }
const hreflangRegionAliases = { uk: 'GB' }

const exceptions = ['zh-HK']
/**
 * @param {String} code language code
 * @returns ISO 639-2 code (if we have it mapped)
 */
const getISO639_2LanguageCode = (code = '') => {
  let langCode = code

  // it's a BSP code that we can convert to a two letter code
  if (!exceptions.includes(code) && code.length > 2) {
    langCode = code.slice(0, 2)
  }

  const fallback = languageConfig.iso639Conversions.en
  return languageConfig.iso639Conversions[langCode] ?? fallback
}

/**
 * Builds the locale list for setAppLocales, which drives the language picker, the hreflang
 * alternates and the locales the router resolves in a URL.
 *
 * @param {Boolean} showUrdu whether Urdu is offered
 * @returns the app locales, without 'ur' when Urdu is not offered
 */
const getAppLocales = (showUrdu) => {
  const { additionalLangs } = languageConfig
  return showUrdu ? additionalLangs : additionalLangs.filter((lang) => lang !== 'ur')
}

const getBrowserSeoLocales = (alternateLocales = []) => {
  return alternateLocales
    .map((locale) => {
      if (languageConfig.chineseLocales.includes(locale)) {
        return languageConfig.chineseLangMapping[locale]
      }
      return locale.split('-')[0]
    })
    .filter((locale) => locale !== i18n.language)
}

/**
 * Normalizes a locale string into an SEO-safe `hreflang` value.
 *
 * Rules:
 * - Accepts language-only values (`en`) and language+variant values (`en_US`, `zh-hans`).
 * - Converts underscores to hyphens, trims empty segments, and ignores malformed structures.
 * - Applies configured language and region aliases before validation.
 * - Supports script variants (`zh-Hans`) and region variants (`en-US`).
 * - If the region variant is invalid, falls back to the normalized language.
 *
 * @param {string} locale Raw locale from CMS/content (for example: `en_US`, `eng-uk`, `zh-hans`).
 * @returns {string | null} A normalized locale suitable for `hreflang` (for example: `en-US`, `zh-Hans`, `fr`), or `null` when the input cannot be normalized.
 */
const normalizeHreflangLocale = (locale = '') => {
  if (typeof locale !== 'string' || locale.length === 0) {
    return null
  }

  const localeParts = locale.replaceAll('_', '-').split('-').filter(Boolean)

  if (localeParts.length === 0 || localeParts.length > 2) {
    return null
  }

  const [rawLanguage = '', rawVariant = ''] = localeParts
  const language = hreflangLanguageAliases[rawLanguage.toLowerCase()] ?? rawLanguage.toLowerCase()

  if (!/^[a-z]{2,3}$/.test(language)) {
    return null
  }

  if (!rawVariant) {
    return language
  }

  if (/^[a-z]{4}$/i.test(rawVariant)) {
    const script = `${rawVariant.charAt(0).toUpperCase()}${rawVariant.slice(1).toLowerCase()}`
    return `${language}-${script}`
  }

  const aliasedRegion = hreflangRegionAliases[rawVariant.toLowerCase()]
  const region = aliasedRegion ?? rawVariant.toUpperCase()

  if (!/^[A-Z]{2}$/.test(region)) {
    return language
  }

  return `${language}-${region}`
}

const getHreflangLocales = ({ contentLocale, alternateLocales = [] } = {}) => {
  return [...new Set([contentLocale, ...alternateLocales].map(normalizeHreflangLocale).filter(Boolean))]
}

/**
 * gets the browsers language even if we do not support it in our app
 * @returns the language in the format that frontier uses
 */
const getBrowserLanguage = () => {
  if (navigator.language === 'zh-CN') {
    return 'zh-hans'
  }

  return navigator.language.slice(0, 2)
}

export { getAppLocales, getISO639_2LanguageCode, getBrowserSeoLocales, getHreflangLocales, getBrowserLanguage }
